import router from '@/router'
import { reportStoreError } from '@/store/reportStoreError'
import { sendAgentMessageStream, sendAgentReply } from '@/services/agentApi'
import { sendMessageStream } from '@/services/chatApi'
import {
  fetchConversations,
  createConversation,
  fetchConversation,
  updateConversation,
  deleteConversation,
  generateConversationTitle,
} from '@/services/conversationApi'

export const chat = {
  namespaced: true,
  state: () => ({
    conversations: [],
    currentConversationId: null,
    messages: [],
    notification: null,
    thinking: false,
    initRouting: null,
  }),
  mutations: {
    setCurrentConversation(state, id) {
      state.currentConversationId = id || null
    },
    RESET_STATE(state) {
      state.conversations = []
      state.currentConversationId = null
      state.messages = []
      state.notification = null
      state.thinking = false
    },
    LOAD_CONVERSATIONS(state, conversations) {
      state.conversations = conversations
    },
    SET_MESSAGES(state, messages) {
      state.messages = messages
    },
    ADD_MESSAGE(state, msg) {
      state.messages.push(msg)
    },
    APPEND_TO_LAST_ASSISTANT(state, text) {
      const last = state.messages[state.messages.length - 1]
      if (last && last.role === 'assistant') {
        last.content += text
      }
    },
    FINALIZE_LAST_ASSISTANT(state) {
      const last = state.messages[state.messages.length - 1]
      if (last) last.streaming = false
    },
    SET_ROUTING_ON_LAST_ASSISTANT(state, routing) {
      const last = state.messages[state.messages.length - 1]
      if (last && last.role === 'assistant') {
        last.routing = routing
      }
    },
    SET_THINKING(state, val) {
      state.thinking = val
    },
    SHOW_NOTIFICATION(state, { type, message }) {
      state.notification = { type, message }
    },
    CLEAR_NOTIFICATION(state) {
      state.notification = null
    },
    UPDATE_CONVERSATION(state, { id, data }) {
      const conv = state.conversations.find(c => c.id === id)
      if (conv) Object.assign(conv, data)
    },
    REMOVE_CONVERSATION(state, id) {
      state.conversations = state.conversations.filter(c => c.id !== id)
    },
    SET_INIT_ROUTING(state, routing) {
      state.initRouting = routing
    },
    CLEAR_INIT_ROUTING(state) {
      state.initRouting = null
    },
  },
  actions: {
    async initConversations({ commit }) {
      try {
        const conversations = await fetchConversations()
        commit('LOAD_CONVERSATIONS', conversations)
      } catch (e) {
        reportStoreError('chat/initConversations', e)
        commit('SHOW_NOTIFICATION', { type: 'error', message: `Erreur chargement : ${e.message}` })
      }
    },

    async loadConversation({ commit }, id) {
      try {
        const conversation = await fetchConversation(id)
        commit('setCurrentConversation', id)
        commit('SET_MESSAGES', conversation.messages)
      } catch (e) {
        reportStoreError('chat/loadConversation', e)
        commit('setCurrentConversation', null)
        commit('SET_MESSAGES', [])
        const fallback = router.currentRoute.name === 'project_detail' ? 'project_detail' : 'home'
        router.push({ name: fallback })
      }
    },

    newConversation({ commit }) {
      commit('setCurrentConversation', null)
      commit('SET_MESSAGES', [])
    },

    async sendMessage({ commit, state }, { text, engineId }) {
      let conversationId = state.currentConversationId
      const isFirstExchange = state.messages.length === 0

      // Lazy conversation creation on first message
      if (!conversationId) {
        try {
          const conv = await createConversation(engineId)
          conversationId = conv.id
          commit('setCurrentConversation', conversationId)
          router.push({ name: 'project_detail', params: { id: conversationId } })
        } catch (e) {
          reportStoreError('chat/sendMessage:createConversation', e)
          commit('SHOW_NOTIFICATION', { type: 'error', message: `Impossible de créer la conversation : ${e.message}` })
          return
        }
      }

      commit('ADD_MESSAGE', { role: 'user', content: text })
      commit('SET_THINKING', true)

      try {
        const response = await sendMessageStream(state.messages, engineId, conversationId)
        commit('ADD_MESSAGE', { role: 'assistant', content: '' })

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let done = false
        let firstChunkReceived = false

        while (!done) {
          const { value, done: doneReading } = await reader.read()
          done = doneReading
          const chunk = decoder.decode(value, { stream: true })
          const lines = chunk.split('\n\n').filter(l => l.trim().startsWith('data:'))

          for (const line of lines) {
            const raw = line.replace('data: ', '').trim()
            if (!raw || raw === 'end') continue
            let parsed
            try {
              parsed = JSON.parse(raw)
            } catch (e) {
              continue
            }
            if (parsed.text) {
              if (!firstChunkReceived) {
                commit('SET_THINKING', false)
                firstChunkReceived = true
              }
              commit('APPEND_TO_LAST_ASSISTANT', parsed.text)
            }
          }
        }

        commit('FINALIZE_LAST_ASSISTANT')

        if (isFirstExchange) {
          generateConversationTitle(conversationId)
            .then(({ title }) => commit('UPDATE_CONVERSATION', { id: conversationId, data: { title } }))
            .catch(() => {})
        }

        // Refresh list to reflect updated_at ordering
        const conversations = await fetchConversations()
        commit('LOAD_CONVERSATIONS', conversations)
      } catch (e) {
        reportStoreError('chat/sendMessage:stream', e)
        commit('SET_THINKING', false)
        commit('SHOW_NOTIFICATION', { type: 'error', message: `Erreur réseau : ${e.message}` })
      }
    },

    async sendAgentMessage({ commit, state }, { text, engineId }) {
      let conversationId = state.currentConversationId
      const isFirstExchange = state.messages.length === 0

      if (!conversationId) {
        try {
          const conv = await createConversation(engineId)
          conversationId = conv.id
          commit('setCurrentConversation', conversationId)
          router.push({ name: 'chat_detail', params: { id: conversationId } })
        } catch (e) {
          reportStoreError('chat/sendAgentMessage:createConversation', e)
          commit('SHOW_NOTIFICATION', { type: 'error', message: `Impossible de créer la conversation : ${e.message}` })
          return
        }
      }

      commit('ADD_MESSAGE', { role: 'user', content: text })
      commit('SET_THINKING', true)

      try {
        // Add assistant message with routing: null so the property is reactive in Vue 2
        commit('ADD_MESSAGE', { role: 'assistant', content: '', streaming: true, routing: null })

        let firstChunkReceived = false

        await sendAgentMessageStream(conversationId, text, {
          onRouting(stage1, stage2) {
            commit('SET_ROUTING_ON_LAST_ASSISTANT', { stage1, stage2 })
            commit('SET_THINKING', false)
            firstChunkReceived = true
          },
          onChunk(chunk) {
            if (!firstChunkReceived) {
              commit('SET_THINKING', false)
              firstChunkReceived = true
            }
            commit('APPEND_TO_LAST_ASSISTANT', chunk)
          },
          onError(message) {
            reportStoreError('chat/sendAgentMessage:onError', { message })
            commit('SET_THINKING', false)
            commit('SHOW_NOTIFICATION', { type: 'error', message })
          },
        })

        commit('FINALIZE_LAST_ASSISTANT')

        if (isFirstExchange) {
          generateConversationTitle(conversationId)
            .then(({ title }) => commit('UPDATE_CONVERSATION', { id: conversationId, data: { title } }))
            .catch(() => {})
        }

        const conversations = await fetchConversations()
        commit('LOAD_CONVERSATIONS', conversations)
      } catch (e) {
        reportStoreError('chat/sendAgentMessage:stream', e)
        commit('SET_THINKING', false)
        commit('SHOW_NOTIFICATION', { type: 'error', message: `Erreur réseau : ${e.message}` })
      }
    },

    async sendAgentInitReply({ commit, state }) {
      const conversationId = state.currentConversationId
      if (!conversationId) return

      // Capture routing before CLEAR_INIT_ROUTING fires (it commits synchronously right after dispatch)
      const routing = state.initRouting
      commit('SET_THINKING', true)

      try {
        let messageAdded = false

        // Delay adding the assistant bubble until actual data arrives — avoids the empty "_" placeholder
        const ensureMessage = () => {
          if (!messageAdded) {
            commit('ADD_MESSAGE', { role: 'assistant', content: '', streaming: true, routing: null })
            commit('SET_THINKING', false)
            messageAdded = true
          }
        }

        await sendAgentReply(conversationId, {
          stage1: routing?.stage1,
          stage2: routing?.stage2,
          onRouting(stage1, stage2) {
            ensureMessage()
            commit('SET_ROUTING_ON_LAST_ASSISTANT', { stage1, stage2 })
          },
          onChunk(chunk) {
            ensureMessage()
            commit('APPEND_TO_LAST_ASSISTANT', chunk)
          },
          onTitle(title) {
            commit('UPDATE_CONVERSATION', { id: conversationId, data: { title } })
          },
          onError(message) {
            reportStoreError('chat/sendAgentInitReply:onError', { message })
            commit('SET_THINKING', false)
            commit('SHOW_NOTIFICATION', { type: 'error', message })
          },
        })

        if (messageAdded) commit('FINALIZE_LAST_ASSISTANT')

        const conversations = await fetchConversations()
        commit('LOAD_CONVERSATIONS', conversations)
      } catch (e) {
        reportStoreError('chat/sendAgentInitReply:stream', e)
        commit('SET_THINKING', false)
        commit('SHOW_NOTIFICATION', { type: 'error', message: `Erreur réseau : ${e.message}` })
      }
    },

    async renameConversation({ commit }, { id, title }) {
      try {
        await updateConversation(id, { title })
        commit('UPDATE_CONVERSATION', { id, data: { title } })
        commit('SHOW_NOTIFICATION', { type: 'success', message: 'Conversation renommée.' })
      } catch (e) {
        reportStoreError('chat/renameConversation', e)
        commit('SHOW_NOTIFICATION', { type: 'error', message: `Erreur renommage : ${e.message}` })
      }
    },

    async deleteConversation({ commit, state }, id) {
      try {
        await deleteConversation(id)
        commit('REMOVE_CONVERSATION', id)
        if (state.currentConversationId === id) {
          const fallback = router.currentRoute.name === 'project_detail' ? 'project_detail' : 'home'
          router.push({ name: fallback })
        }
      } catch (e) {
        reportStoreError('chat/deleteConversation', e)
        commit('SHOW_NOTIFICATION', { type: 'error', message: `Erreur suppression : ${e.message}` })
      }
    },
  },
  getters: {
    lastConversationId(state) {
      return state.conversations.length > 0 ? state.conversations[0].id : null
    },
  },
}
